/* istanbul ignore file */

import { Module } from 'vuex';

import { AppState } from '@/store';
import { prefixValues } from '@/helpers/util';
import { HeaderOptions, HeaderFlags, FooterOptions, FooterMode } from './header';
import { Routes } from '@/router';
import {
  CustomerDeliveryAddress,
  FormType,
  TokenizedPaymentCard,
  Promotion,
  BasicOrder,
  RecentOrFavoriteOrder,
  Epop,
  OrderWithContext,
  LastSelectedElement,
  Reward,
  Campaign,
  Achievement,
  IngredientMetadata, 
  Recipe,
  CreateUserRequest,
  Restaurant,
  RewardWithPromoContext,
  RecentOrFavoriteEntree,
  RecentOrFavoriteEntreeContents,
  MenuItem,
  Menu,
} from '@/models';
import { ButtonType } from '@/components/CmgButton/cmg-button';
import { LoyaltyExplainerModalState } from '@/components/modals/CmgLoyaltyExplainerModal/cmg-loyalty-explainer-modal';
import { countries } from '@/constants';

export * from './header';

const MUTATION_NAMES = {
  SET_BAG_VISIBILITY: 'setBagVisibility',
  SET_BAG_ICON_VISIBILITY: 'setBagIconVisibility',
  REWARD_CONFIRMATION_VISIBILITY: 'setRewardConfirmationVisibility',
  SET_COUNTRY_PROFILE: 'setCountryProfile',
  SET_APP_SCROLLABILITY: 'setAppScrollability',
  SET_MODAL_VISIBILITY: 'setModalVisibility',
  SET_HEADER_OPTIONS: 'setHeaderOptions',
  SET_FOOTER_OPTIONS: 'setFooterOptions',
  INCREMENT_LOADER: 'incrementLoader',
  DECREMENT_LOADER: 'decrementLoader',
  SET_SHOULD_SCROLL_HOME_TO_HASH: 'setShouldScrollHomeToHash',
  SET_IS_AEM_HOMEPAGE_ENABLED: 'setIsAemHomepageEnabled',
  SET_CLIENT_WIDTH: 'setClientWidth',
  SET_BANNER_STATE: 'setBannerState',
  SET_CELEBRATION_POINTS_VISIBILITY: 'setCelebrationPointsVisibility',
  SET_ENROLLING_IN_REWARDS: 'setEnrollingInRewards',
  SET_APP_PEPPER_VISIBILITY: 'setPepperVisibility',
  SET_SHOULD_SHOW_DELIVERY_PICK_UP_MODAL: 'setShouldShowDeliveryPickUpModal',
  SET_SESSION_EXPIRED_BANNER: 'setSessionExpiredBanner',
  SET_SHOW_VERIFY_MOBILE_NUMBER: 'setShowVerifyMobileNumber',
  SET_HAS_FAILED_2SV: 'setHasFailed2SV',
  SET_TWO_STEP_VERIFICATION_WELCOME: 'setTwoStepVerificationWelcome',

  // Used for ADA focus tracking
  SET_LAST_SELECTED_ELEMENT: 'setLastSelectedElement',

  // Blurs entire page
  SET_APP_BLURRED: 'setAppBlurred',

  // Blurs content area, which is behind modals and bag
  SET_CONTENT_BLURRED: 'setContentBlurred',

  SET_TOAST_VISIBILITY: 'setToastVisibility',

  SET_CHURROS_TOAST_ALREADY_DISPLAYED: 'setChurrosToastAlreadyDisplayed',
  SET_DIETARY_FILTER_HINT_SHOWN: 'setDietaryFilterHintShown',
  SET_REWARD_TOAST_VISIBILITY: 'setRewardToastVisibility',
  SET_SIGNIN_SIGNUP_VISIBILITY_EXP_A: 'setSignInSignUpVisibilityExperienceA',
  SET_SIGNIN_SIGNUP_VISIBILITY_EXP_B: 'setSignInSignUpVisibilityExperienceB',
  SET_MOBILE_BUILDER_NAV_EXPERIENCE_A: 'setMobileBuilderNavExperienceA',
  SET_MOBILE_BUILDER_NAV_EXPERIENCE_B: 'setMobileBuilderNavExperienceB',
  SET_INGREDIENT_PANEL_VISIBILITY: 'setIngredientPanelVisibility',
  SET_RECIPE: 'setRecipe',
  SET_INGREDIENTS: 'setIngredients',
  SET_INGREDIENT: 'setIngredient',
  SET_RECIPE_BANNER_EXPANDED: 'setRecipeBannerExpanded',
  SET_RECIPE_VISIBLE: 'setRecipeVisible',
  SET_RECIPE_DISTANCE: 'setRecipeDistanceLeft',
  SET_UPDATED_MOBILE_NUMBER_ON_LOGIN: 'setUpdatedMobileNumberOnLogin'
};

export const ACTION_NAMES = {
  SET_REWARD_TOAST_VISIBILITY: 'setRewardToastVisibility',
}
export const ACTIONS = prefixValues(ACTION_NAMES, 'ui/');
export const MUTATIONS = prefixValues(MUTATION_NAMES, 'ui/');

// TODO: move the models and constants into the models folder
export enum CountryCode {
  US = 'US',
  CA = 'CA',
}

export interface CountryStrings {
  [key: string]: string;
}

export interface CountryProfile {
  countryCode: string;
  name: string;
  lat: number;
  lng: number;
  flagSource?: string;
  currencySymbol: string;
  currencySymbolShort: string;
  topLevelDomain: string;
  strings: CountryStrings;
  locale: string;
}

export interface TwoStepVerification {
  email: string;
  phoneNumber: string;
  requiresPasswordVerification: boolean;
  requiresSignOutOnFailure: boolean;
  onSuccess: () => Promise<void>;
  onFailure: () => void;
  onCancel?: () => void;
}

export interface ModalState<TArgs = void> {
  visible: boolean;
  args?: TArgs;
}

export interface Modals {
  rewardsCelebration: ModalState<{
    newRewardCount: number;
  }>;
  rewards: ModalState;
  rewardsListModal: ModalState<{rewards?:Promotion[]}>;
  account: ModalState;
  editAccountModal: ModalState;
  editCountryModal: ModalState;
  lostBagModal: ModalState;
  updatePasswordModal: ModalState;
  leaveRewardsModal: ModalState;
  addCreditOrDebitCardModal: ModalState<{
    oneTimePaymentUse?: boolean;
    providerId?: string;
    onClose?: () => void;
  }>;
  addGiftCardModal: ModalState;
  removePaymentCardModal: ModalState<{
    token: TokenizedPaymentCard;
  }>;
  cancelGroupOrder: ModalState;
  deliveryAddress: ModalState<{
    isSavedAddress?: boolean;
    deliveryAddress: CustomerDeliveryAddress | null;
    afterClose?: () => void;
  }>;
  repromptCountryProfileModal: ModalState;
  countrySwitchAlertModal: ModalState;
  signIn: ModalState<{
    /** Route to navigate to after successful sign in */
    route?: Routes | null;
    type?: FormType | null;
    onClose?: () => void;
    prefill?: CreateUserRequest;
  }>;
  verifyMobileNumber: ModalState<TwoStepVerification>;
  twoStepVerificationWelcome: ModalState<TwoStepVerification>;


  /**
   * Note that with the current setup the callback must be called before setModalVisibility( false ) is called
   * or the args will have already been cleared
   */
  findAChipotle: ModalState<{ onClose?: () => void; mode?: string; pickupSearchInput?: string; deliverySearchInput?: string, suppressPickupDeliveryToast?: boolean }>;

  favoriteRestaurant: ModalState<{ restaurantNumber?: number }>;

  mealBuilderMealName: ModalState<{
    beforeClose: (mealName: string) => Promise<any>;
    mealName?: string;
  }>;

  checkoutBadPhoneNumber: ModalState<{ phoneNumber: string | null }>;
  checkoutPaymentIssue: ModalState<{
    order: OrderWithContext;
    isDelivery: boolean;
    onClickSwitchToPayInStore: () => Promise<void>;
    onTryAgain: () => Promise<void>;
    onClose?: () => void;
  }>;

  addReward: ModalState;

  achievedBadge: ModalState<{ achievement: Achievement }>;

  celebrateAchievement: ModalState<{ achievement: Achievement }>;

  rewardDetail: ModalState<{ reward: Reward }>;

  bagOffer: ModalState<{ prevIndex: number }>;

  rewardExtrasCampaignDetail: ModalState<{ campaign: Campaign }>;

  rewardRedemptionSuccess: ModalState<RewardWithPromoContext>;

  rewardTermsAndConditions: ModalState<{ termsAndConditions?: string; onBack?: () => void }>;

  rewardsExchangeWelcome: ModalState;

  rewardsSpendYourPoints: ModalState;

  rewardQR: ModalState<{ reward?: Promotion }>;

  basicError: ModalState<{
    header: string | null;
    content: string | null;
    background?: string;
    options: {
      text: string;
      type?: ButtonType;
      onClick: () => void | Promise<any>;
      /** Set this to true inside the onClick event to make the button's loading spinner show */
      isLoading?: boolean;
    }[];
    onClose?: () => void;
    isShort?: boolean;
    unmaskContent?: boolean;
  }>;

  loyaltyExplainer: ModalState<{ state: LoyaltyExplainerModalState; existingLoyaltyUser?: boolean }>;

  feedback: ModalState;

  retroCreditReceiptModal: ModalState<{ imageUrl: string }>;
  retroCreditConfirmNavModal: ModalState<{ onConfirm: () => void; onCancel: () => void }>;

  fullOrderDetails: ModalState<{ order: BasicOrder; name: string; onClickAddToBag: () => Promise<void> }>;
  addFavoriteOrder: ModalState<{ order: RecentOrFavoriteOrder }>;
  leaveGroupOrder: ModalState;
  noLoyaltyForYou: ModalState;
  toast: ModalState<{
    title?: string;
    subTitle?: string;
    body: string;
    icon?: string;
    bodyOnly?: boolean;
    legalCopy?: string;
    isUpdatedVersion?: boolean;
    visibleTimeLength?: number;
    unmaskContent?: boolean;
    after?: () => void;
  }>;
  bagReconciliation: ModalState<{
    restaurantId: number;
    isPickup: boolean;
    itemsIds: string[];
    onCancel: () => void;
    onRemoveItems: () => void;
  }>;
  epop: ModalState<{
    epops: Epop[];
  }>;
  globalErrorToast: ModalState;
  giftCardQr: ModalState;
  deliveryOfflineModal: ModalState<{
    onSwitchToPickup: () => Promise<void>;
    onClose?: () => void;
  }>;
  dietaryFiltersModal: ModalState;
  rewardsCountryMismatch: ModalState;
  subscribeEmailMarketing: ModalState;
  mobileNutritionDetails: ModalState;
  guacModeVerified: ModalState;
  guacModeJoinRewards: ModalState;
  guacModeSignIn: ModalState<{
    type?: FormType | null;
  }>;
  rewardsMembersOnlyModal: ModalState;
  twoStepVerificationLearnMoreModal: ModalState;
  exitMobileVerificationModal: ModalState<{
    onLeave: () => void;
  }>;
  verificationSystemErrorModal: ModalState<{
    requiresSignOutOnFailure?: boolean;
    onLeave: () => Promise<void>;
  }>;
  clearWalletModal: ModalState;
  lockedOutModal: ModalState;
  pickupOptionsModal: ModalState<{restaurant: Restaurant}>;
  maxEntreeLimitModal: ModalState<{
    maxEntreeLimitNumber: string;
  }>;
  oneTapReorderDetailsModal: ModalState<{
    orderDate: string;
    entree: RecentOrFavoriteEntree | undefined;
    onAddToBag: () => void;
  }>;
  oneTapReorderUnavailabilityModal: ModalState<{
    entree: RecentOrFavoriteEntree;
    unavailableItems: MenuItem[];
    onAddToBag: (updatedEntree: RecentOrFavoriteEntree) => void;
  }>;
}

/**
 * Note that with the current setup the callback must be called before setModalVisibility( false ) is called
 * or the args will have already been cleared
 */
export interface FindAChipotleModalArgs {
  onClose: () => void;
}

export interface SetModalVisibilityArgs<TModal extends keyof Modals> {
  modal: TModal;
  state: {
    visible: boolean;
    args: Modals[TModal]['args'] | null;
  };
}

// Track number of things loading so a parent object can show/hide
// a loader without needing events from children
export interface Loaders {
  bag: number;
  pickupTimes: number;
}

export interface BannerState {
  visible: boolean;
  message: string;
  details?: string;
  backgroundColor?: string;
  timeoutMs?: number;
}

export interface UiModuleState {
  bag: {
    visible: boolean;
    iconVisible: boolean;
    rewardConfirmation: boolean;
    bagToastVisibility: boolean;
  };

  ingredientsPage: { 
    ingredientsPanelVisibility: boolean;
    selectedRecipe: Recipe | null;
    visibleIngredients: IngredientMetadata[];
    selectedIngredient: IngredientMetadata | null;
    isRecipeBannerExpanded: boolean;
    selectedRecipeDistance: number;
    isSelectedRecipeVisible: boolean;
  };

  app: {
    scrollable: boolean;
    blurred: boolean;
    contentBlurred: boolean;
    pepperVisibility: boolean;
  };

  countryProfile: CountryProfile;
  modals: Modals;
  loaders: Loaders;

  header: {
    options: HeaderOptions;
  };

  footer: {
    options: FooterOptions;
  };

  home: {
    shouldScrollToHash: boolean;
    isAemHomepageEnabled: boolean;
  };

  orderConfirmation: {
    hasFailed2SV: boolean;
  }

  clientWidth: number;
  notificationBanner: BannerState;
  pointsCelebrationVisible: boolean;
  enrollingInRewards: boolean;
  shouldShowDeliveryPickUpModal: boolean;
  lastSelectedElement: LastSelectedElement | null;
  toastVisibility: boolean;
  churrosToastAlreadyDisplayed: boolean;
  dietaryFilterHintShown: boolean;
  signInSignUpVisibilityExperienceA: boolean;
  signInSignUpVisibilityExperienceB: boolean;
  mobileBuilderNavExperienceA: boolean;
  mobileBuilderNavExperienceB: boolean;
  showSessionExpiredBanner: boolean;
  showVerifyMobileNumber: boolean;
  selectedUpdateMobileNumberOnLogin: boolean;
}

const DEFAULT_STATE: UiModuleState = {
  bag: {
    visible: false,
    iconVisible: true,
    rewardConfirmation: false,
    bagToastVisibility: false
  },
  ingredientsPage: {
    ingredientsPanelVisibility: false,
    selectedRecipe: null,
    visibleIngredients: [],
    selectedIngredient: null,
    isRecipeBannerExpanded: false,
    isSelectedRecipeVisible: false,
    selectedRecipeDistance: 0
  },
  app: {
    scrollable: true,
    blurred: false,
    contentBlurred: false,
    pepperVisibility: false,
  },
  countryProfile: {
    name: countries.us.name,
    lat: countries.us.lat,
    lng: countries.us.lng,
    countryCode: countries.us.countryCode,
    currencySymbol: countries.us.currencySymbol,
    currencySymbolShort: countries.us.currencySymbolShort,
    topLevelDomain: countries.us.topLevelDomain,
    strings: countries.us.strings,
    locale: countries.us.locale,
  },
  modals: {
    signIn: { visible: false },
    rewardsListModal: { visible: false },
    verifyMobileNumber: { visible: false },
    twoStepVerificationWelcome: { visible: false },
    rewards: { visible: false },
    account: { visible: false },
    countrySwitchAlertModal: { visible: false },
    editAccountModal: { visible: false },
    editCountryModal: { visible: false },
    lostBagModal: { visible: false },
    updatePasswordModal: { visible: false },
    leaveRewardsModal: { visible: false },
    findAChipotle: { visible: false },
    favoriteRestaurant: { visible: false },
    addCreditOrDebitCardModal: { visible: false },
    addGiftCardModal: { visible: false },
    removePaymentCardModal: { visible: false },
    cancelGroupOrder: { visible: false },
    deliveryAddress: { visible: false },
    mealBuilderMealName: { visible: false },
    basicError: { visible: false },
    checkoutBadPhoneNumber: { visible: false },
    checkoutPaymentIssue: { visible: false },
    addReward: { visible: false },
    achievedBadge: { visible: false },
    celebrateAchievement: { visible: false },
    rewardDetail: { visible: false },
    bagOffer: { visible: false },
    rewardExtrasCampaignDetail: { visible: false },
    rewardRedemptionSuccess: { visible: false },
    rewardTermsAndConditions: { visible: false },
    rewardsExchangeWelcome: { visible: false },
    rewardsSpendYourPoints: { visible: false },
    rewardQR: { visible: false },
    loyaltyExplainer: { visible: false },
    feedback: { visible: false },
    repromptCountryProfileModal: { visible: false },
    retroCreditReceiptModal: { visible: false },
    retroCreditConfirmNavModal: { visible: false },
    fullOrderDetails: { visible: false },
    addFavoriteOrder: { visible: false },
    leaveGroupOrder: { visible: false },
    rewardsCelebration: { visible: false },
    noLoyaltyForYou: { visible: false },
    toast: { visible: false },
    bagReconciliation: { visible: false },
    epop: { visible: false },
    globalErrorToast: { visible: false },
    giftCardQr: { visible: false },
    deliveryOfflineModal: { visible: false },
    dietaryFiltersModal: { visible: false },
    rewardsCountryMismatch: { visible: false },
    subscribeEmailMarketing: { visible: false },
    mobileNutritionDetails: { visible: false },
    guacModeVerified: { visible: false },
    guacModeJoinRewards: { visible: false },
    guacModeSignIn: { visible: false },
    rewardsMembersOnlyModal: { visible: false },
    twoStepVerificationLearnMoreModal: { visible: false },
    exitMobileVerificationModal: { visible: false },
    verificationSystemErrorModal: { visible: false },
    clearWalletModal: { visible: false },
    lockedOutModal: { visible: false },
    pickupOptionsModal: { visible: false },
    maxEntreeLimitModal: { visible: false },
    oneTapReorderDetailsModal: { visible: false },
    oneTapReorderUnavailabilityModal: { visible: false }
  },
  loaders: {
    bag: 0,
    pickupTimes: 0,
  },
  header: {
    options: { flags: HeaderFlags.All & ~HeaderFlags.ReturnToRoute & ~HeaderFlags.LogoVisibleXS },
  },
  footer: {
    options: { mode: FooterMode.Normal },
  },
  home: {
    shouldScrollToHash: false,
    isAemHomepageEnabled: false
  },
  orderConfirmation: {
    hasFailed2SV: false,
  },
  clientWidth: 0,
  notificationBanner: { visible: false, message: '' },
  pointsCelebrationVisible: false,
  enrollingInRewards: false,
  shouldShowDeliveryPickUpModal: false,
  lastSelectedElement: null,
  toastVisibility: false,
  churrosToastAlreadyDisplayed: false,
  dietaryFilterHintShown: false,
  signInSignUpVisibilityExperienceA: false,
  signInSignUpVisibilityExperienceB: false,
  mobileBuilderNavExperienceA: false,
  mobileBuilderNavExperienceB: false,
  
  showSessionExpiredBanner: false,
  showVerifyMobileNumber: false,
  selectedUpdateMobileNumberOnLogin: false,
};

export const ALL_MODAL_STRINGS = Object.keys(DEFAULT_STATE.modals);

export function makeUiModule(): Module<UiModuleState, AppState> {
  return {
    namespaced: true,
    state: DEFAULT_STATE,
    actions: {
      [ACTION_NAMES.SET_REWARD_TOAST_VISIBILITY]: ({ commit, state, getters }) => {
        commit(MUTATION_NAMES.SET_REWARD_TOAST_VISIBILITY, true);
        setTimeout(() => {
          commit(MUTATION_NAMES.SET_REWARD_TOAST_VISIBILITY, false);
        }, 8000);
        
      },
    },
    mutations: {
      [MUTATION_NAMES.SET_REWARD_TOAST_VISIBILITY]: (state, visible: boolean) => {
        state.bag.bagToastVisibility = visible;
      },
      [MUTATION_NAMES.SET_BAG_VISIBILITY]: (state, visible) => {
        state.bag.visible = visible;
      },
      [MUTATION_NAMES.SET_BAG_ICON_VISIBILITY]: (state, visible) => {
        state.bag.iconVisible = visible;
      },
      [MUTATION_NAMES.REWARD_CONFIRMATION_VISIBILITY]: (state, visible) => {
        state.bag.rewardConfirmation = visible;
        setTimeout(() => {
          state.bag.rewardConfirmation = false;
        }, 3000);
      },
      [MUTATION_NAMES.SET_APP_SCROLLABILITY]: (state, scrollable) => {
        state.app.scrollable = scrollable;
      },
      [MUTATION_NAMES.SET_APP_BLURRED]: (state, blurred) => {
        state.app.blurred = blurred;
      },
      [MUTATION_NAMES.SET_COUNTRY_PROFILE]: (state, countryProfile) => {
        state.countryProfile = countryProfile;
      },
      [MUTATION_NAMES.SET_CONTENT_BLURRED]: (state, blurred) => {
        state.app.contentBlurred = blurred;
      },
      [MUTATION_NAMES.SET_MODAL_VISIBILITY]: (state, args: SetModalVisibilityArgs<any>) => {
        (state.modals as any)[args.modal] = args.state;
      },
      [MUTATION_NAMES.SET_HEADER_OPTIONS]: (state, headerOptions: HeaderOptions) => {
        state.header.options = headerOptions;
      },
      [MUTATION_NAMES.SET_FOOTER_OPTIONS]: (state, options: FooterOptions) => {
        state.footer.options = options;
      },
      [MUTATION_NAMES.INCREMENT_LOADER]: (state, loader: keyof Loaders) => {
        state.loaders[loader]++;
      },
      [MUTATION_NAMES.DECREMENT_LOADER]: (state, loader: keyof Loaders) => {
        state.loaders[loader]--;
      },
      [MUTATION_NAMES.SET_SHOULD_SCROLL_HOME_TO_HASH]: (state, value: boolean) => {
        state.home.shouldScrollToHash = value;
      },
      [MUTATION_NAMES.SET_IS_AEM_HOMEPAGE_ENABLED]: (state, value: boolean) => {
        state.home.isAemHomepageEnabled = value;
      },
      [MUTATION_NAMES.SET_HAS_FAILED_2SV]: (state, value: boolean) => {
        state.orderConfirmation.hasFailed2SV = value;
      },
      [MUTATION_NAMES.SET_CLIENT_WIDTH]: (state, width: number) => {
        state.clientWidth = width;
      },
      [MUTATION_NAMES.SET_BANNER_STATE]: (state, bannerState: BannerState) => {
        state.notificationBanner = bannerState;
      },
      [MUTATION_NAMES.SET_CELEBRATION_POINTS_VISIBILITY]: (state, isVisible: boolean) => {
        state.pointsCelebrationVisible = isVisible;
      },
      [MUTATION_NAMES.SET_ENROLLING_IN_REWARDS]: (state, enrolling: boolean) => {
        state.enrollingInRewards = enrolling;
      },
      [MUTATION_NAMES.SET_APP_PEPPER_VISIBILITY]: (state, visible: boolean) => {
        state.app.pepperVisibility = visible;
      },
      [MUTATION_NAMES.SET_SHOULD_SHOW_DELIVERY_PICK_UP_MODAL]: (state, shouldShow: boolean) => {
        state.shouldShowDeliveryPickUpModal = shouldShow;
      },
      [MUTATION_NAMES.SET_LAST_SELECTED_ELEMENT]: (state, lastSelectedElement: LastSelectedElement) => {
        state.lastSelectedElement = lastSelectedElement;
      },
      [MUTATION_NAMES.SET_TOAST_VISIBILITY]: (state, visible: boolean) => {
        state.toastVisibility = visible;
      },
      [MUTATION_NAMES.SET_CHURROS_TOAST_ALREADY_DISPLAYED]: (state, displayed: boolean) => {
        state.churrosToastAlreadyDisplayed = displayed;
      },
      [MUTATION_NAMES.SET_DIETARY_FILTER_HINT_SHOWN]: (state, dietaryFilterHintShown: boolean) => {
        state.dietaryFilterHintShown = dietaryFilterHintShown;
      },
      [MUTATION_NAMES.SET_RECIPE]: (state, recipe: Recipe) => {
        state.ingredientsPage.selectedRecipe = recipe;
      },
      [MUTATION_NAMES.SET_INGREDIENT_PANEL_VISIBILITY]: (state, visible: boolean) => {
        state.ingredientsPage.ingredientsPanelVisibility = visible;
      },
      [MUTATION_NAMES.SET_INGREDIENTS]: (state, ingredients: IngredientMetadata[]) => {
        state.ingredientsPage.visibleIngredients = ingredients;
      },
      [MUTATION_NAMES.SET_INGREDIENT]: (state, ingredient: IngredientMetadata) => {
        state.ingredientsPage.selectedIngredient = ingredient;
      },
      [MUTATION_NAMES.SET_RECIPE_BANNER_EXPANDED]: (state, isExpanded: boolean) => {
        state.ingredientsPage.isRecipeBannerExpanded = isExpanded;
      },
      [MUTATION_NAMES.SET_RECIPE_VISIBLE]: (state, isVisible: boolean) => {
        state.ingredientsPage.isSelectedRecipeVisible = isVisible;
      },
      [MUTATION_NAMES.SET_RECIPE_DISTANCE]: (state, distance: number) => {
        state.ingredientsPage.selectedRecipeDistance = distance;
      },
      [MUTATION_NAMES.SET_SIGNIN_SIGNUP_VISIBILITY_EXP_A]: (state, visible: boolean) => {
        state.signInSignUpVisibilityExperienceA = visible;
      },
      [MUTATION_NAMES.SET_SIGNIN_SIGNUP_VISIBILITY_EXP_B]: (state, visible: boolean) => {
        state.signInSignUpVisibilityExperienceB = visible;
      },
      [MUTATION_NAMES.SET_MOBILE_BUILDER_NAV_EXPERIENCE_A]: (state, visible: boolean) => {
        state.mobileBuilderNavExperienceA = visible;
      },
      [MUTATION_NAMES.SET_MOBILE_BUILDER_NAV_EXPERIENCE_B]: (state, visible: boolean) => {
        state.mobileBuilderNavExperienceA = visible;
      },
      [MUTATION_NAMES.SET_SESSION_EXPIRED_BANNER]: (state, showSessionExpiredBanner: boolean) => {
        state.showSessionExpiredBanner = showSessionExpiredBanner;
      },
      [MUTATION_NAMES.SET_SHOW_VERIFY_MOBILE_NUMBER]: (state, showVerifyMobileNumber: boolean) => {
        state.showVerifyMobileNumber = showVerifyMobileNumber;
      },
      [MUTATION_NAMES.SET_UPDATED_MOBILE_NUMBER_ON_LOGIN]: (state, selectedUpdateMobileNumber: boolean) => {
        state.selectedUpdateMobileNumberOnLogin = selectedUpdateMobileNumber;
      },
    },
  };
}
